"use client";

import Link from "next/link";
import { useCallback, useEffect, useState, type FormEvent } from "react";

import { followApi, postsApi } from "@/lib/client-api";
import { isUsableWebSession, useAuthStore } from "@/lib/auth-store";
import { authHrefFor } from "@/lib/navigation";
import { useMounted } from "@/lib/use-mounted";
import { demoCommunityPosts } from "@/lib/demo-data";
import type { PostComment, PostItem } from "@/lib/types";

type LoadState = "loading" | "ready" | "error";

/**
 * Post detail + conversation. Reads are public (anonymous visitors see the
 * post and comments); like and comment actions route signed-out users
 * through /auth.
 */
export function PostDetailClient({ postId }: { postId: string }) {
  const mounted = useMounted();
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const demoMode = useAuthStore((state) => state.demoMode);
  const hasSession = mounted ? isUsableWebSession({ user, accessToken, demoMode }) : false;

  const [post, setPost] = useState<PostItem | null>(null);
  const [comments, setComments] = useState<PostComment[]>([]);
  const [authorHandle, setAuthorHandle] = useState<string | null>(null);
  const [status, setStatus] = useState<LoadState>("loading");
  const [error, setError] = useState("");
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [commentText, setCommentText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!mounted) return;
    setStatus("loading");
    setError("");

    if (demoMode) {
      const demoPost = Object.values(demoCommunityPosts)
        .flat()
        .find((item) => item.id === postId);
      if (demoPost) {
        setPost(demoPost);
        setLikesCount(demoPost.likesCount);
        setComments([]);
        setStatus("ready");
      } else {
        setError("This demo session only includes community feed posts.");
        setStatus("error");
      }
      return;
    }

    try {
      const [detail, commentRows] = await Promise.all([
        postsApi.detail(postId),
        postsApi.comments(postId).catch(() => [] as PostComment[]),
      ]);
      setPost(detail);
      setComments(commentRows);
      setLiked(detail.isLiked === true);
      setLikesCount(detail.likesCount);
      setStatus("ready");
      if (hasSession) {
        // Handle resolution uses the JWT-guarded id lookup; anonymous
        // visitors simply get an unlinked author name.
        void followApi
          .publicProfile(detail.authorId)
          .then((profile) => setAuthorHandle(profile.username ?? null))
          .catch(() => setAuthorHandle(null));
      }
    } catch (err) {
      setError(formatApiError(err));
      setStatus("error");
    }
  }, [demoMode, hasSession, mounted, postId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleLike = async () => {
    if (busy || demoMode || !post) return;
    setBusy(true);
    try {
      if (liked) {
        await postsApi.unlike(postId);
        setLiked(false);
        setLikesCount((count) => Math.max(0, count - 1));
      } else {
        await postsApi.like(postId);
        setLiked(true);
        setLikesCount((count) => count + 1);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitComment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = commentText.trim();
    if (!text || busy || demoMode || !post) return;
    setBusy(true);
    setError("");
    try {
      const created = await postsApi.addComment(postId, text);
      setComments((current) => [...current, created]);
      setCommentText("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!mounted || status === "loading") {
    return (
      <div className="page">
        <div className="empty-panel">Loading post...</div>
      </div>
    );
  }

  if (status === "error" || !post) {
    return (
      <div className="page">
        <div className="empty-panel">
          <div className="notice notice-error">{error || "Could not load this post."}</div>
          <Link href="/" className="btn btn-ghost btn-small" style={{ marginTop: 10 }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <article className="post-card" style={{ maxWidth: 720, margin: "0 auto" }}>
        <div className="post-author">
          {post.authorAvatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={post.authorAvatarUrl} alt="" />
          ) : (
            <span>{initials(post.authorDisplayName)}</span>
          )}
          <div>
            <h3>
              {authorHandle ? (
                <Link href={`/u/${authorHandle}`}>{post.authorDisplayName}</Link>
              ) : (
                post.authorDisplayName
              )}
            </h3>
            <p>{[post.authorRole, post.locationName].filter(Boolean).join(" - ")}</p>
          </div>
        </div>

        {post.text ? <p className="post-text">{post.text}</p> : null}
        {post.imageUrls.length > 0 ? (
          <div className="post-detail-media">
            {post.imageUrls.map((url, index) => (
              <figure className="post-detail-image-frame" key={url}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={url} alt={`Post image ${index + 1}`} />
              </figure>
            ))}
          </div>
        ) : null}
        {post.hashtags.length > 0 ? (
          <div className="chips" style={{ marginTop: 10 }}>
            {post.hashtags.map((tag) => (
              <span key={tag} className="chip">#{tag}</span>
            ))}
          </div>
        ) : null}

        <div className="row-actions" style={{ marginTop: 14 }}>
          {hasSession ? (
            <button
              type="button"
              className={liked ? "btn btn-primary btn-small" : "btn btn-ghost btn-small"}
              onClick={toggleLike}
              disabled={busy || demoMode}
            >
              {liked ? "Liked" : "Like"} · {likesCount.toLocaleString()}
            </button>
          ) : (
            <Link href={authHrefFor(`/posts/${postId}`, "Like this post")} className="btn btn-ghost btn-small">
              Like · {likesCount.toLocaleString()}
            </Link>
          )}
          <span className="chip">{post.commentsCount.toLocaleString()} comments</span>
          {authorHandle ? (
            <Link href={`/u/${authorHandle}`} className="btn btn-ghost btn-small">
              View profile
            </Link>
          ) : null}
        </div>
      </article>

      <section id="comments" className="section" style={{ maxWidth: 720, margin: "0 auto" }}>
        <div className="section-head">
          <div>
            <div className="eyebrow">Conversation</div>
            <h2>Comments</h2>
          </div>
        </div>

        {error ? <div className="notice notice-error">{error}</div> : null}

        {comments.length === 0 ? (
          <div className="empty-panel">No comments yet. Start the conversation.</div>
        ) : (
          <div className="section-block">
            {comments.map((comment) => (
              <div key={comment.id} className="form-card" style={{ marginBottom: 10 }}>
                <div className="post-author">
                  {comment.authorAvatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={comment.authorAvatarUrl} alt="" />
                  ) : (
                    <span>{initials(comment.authorDisplayName)}</span>
                  )}
                  <div>
                    <h3>{comment.authorDisplayName}</h3>
                    <p>{comment.createdAt ? new Date(comment.createdAt).toLocaleString() : ""}</p>
                  </div>
                </div>
                <p style={{ marginTop: 8 }}>{comment.text}</p>
              </div>
            ))}
          </div>
        )}

        {hasSession ? (
          <form onSubmit={submitComment} style={{ display: "grid", gap: 10, marginTop: 14 }}>
            <textarea
              className="textarea"
              value={commentText}
              onChange={(event) => setCommentText(event.target.value)}
              placeholder={demoMode ? "Commenting is disabled in demo mode." : "Add a comment..."}
              maxLength={1000}
              rows={3}
              disabled={demoMode}
            />
            <div className="row-actions">
              <button className="btn btn-primary btn-small" type="submit" disabled={!commentText.trim() || busy || demoMode}>
                {busy ? "Posting..." : "Post comment"}
              </button>
            </div>
          </form>
        ) : (
          <div className="row-actions" style={{ marginTop: 14 }}>
            <Link href={authHrefFor(`/posts/${postId}`, "Join the conversation")} className="btn btn-primary btn-small">
              Sign in to comment
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function formatApiError(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { message?: unknown } } }).response;
    const message = response?.data?.message;
    if (Array.isArray(message)) return message.join(", ");
    if (typeof message === "string") return message;
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}
